import React, { useCallback, useEffect, useState } from "react";
import { Users, PauseCircle, CheckCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText, btnLight } from "./api";

const ROLE_HINT = {
  other: "Designation did not map to a known function — e.g. field reps, technicians, procurement. Review before sending.",
};

function RoleRow({ r, busy, onAct }) {
  const unsent = r.approved + r.held;
  return (
    <li className="px-4 py-2.5 border-t border-[#F0EBDC] flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid={`role-row-${r.role}`}>
      <div className="flex-1 min-w-[220px]">
        <span className="text-[13.5px] font-medium text-[#0F1825]">{r.label}</span>
        {ROLE_HINT[r.role] && <div className="text-[11.5px] text-[#8A5A0B] mt-0.5">{ROLE_HINT[r.role]}</div>}
      </div>
      <span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-[#5C6470] min-w-[300px]" data-testid={`role-counts-${r.role}`}>
        {r.total} contact{r.total === 1 ? "" : "s"} · <strong className={r.approved ? "text-[#3F6D55]" : ""}>{r.approved} approved</strong> · <strong className={r.held ? "text-[#8A5A0B]" : ""}>{r.held} on hold</strong> · <strong className="text-[#1F4E8C]">{r.sent} sent</strong>
      </span>
      <div className="flex items-center gap-2">
        <button type="button" disabled={!!busy || r.approved === 0} onClick={() => onAct(r.role, "hold")} className={btnLight} data-testid={`role-hold-${r.role}`} title="Unapprove every unsent contact of this function — nothing is sent to them until you approve again">
          {busy === `hold:${r.role}` ? <Loader2 size={12} className="animate-spin" /> : <PauseCircle size={12} />} Hold {r.approved ? `(${r.approved})` : ""}
        </button>
        <button type="button" disabled={!!busy || r.held === 0} onClick={() => onAct(r.role, "approve")} className={btnLight} data-testid={`role-approve-${r.role}`} title="Approve every held draft of this function (same greeting + already-emailed checks as Approve all)">
          {busy === `approve:${r.role}` ? <Loader2 size={12} className="animate-spin" /> : <CheckCheck size={12} />} Approve {r.held ? `(${r.held})` : ""}
        </button>
        {unsent === 0 && <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[#A09680]">all sent</span>}
      </div>
    </li>
  );
}

export function RolePanel({ refreshKey, onChanged, readOnly = false }) {
  const [roles, setRoles] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try { setRoles((await api.roles()).roles); } catch (e) { toast.error(errText(e)); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!roles || roles.length === 0) return null;
  const act = async (role, action) => {
    setBusy(`${action}:${role}`);
    try {
      const r = await api.roleAction(role, action);
      toast.success(action === "hold" ? `${r.held} contact${r.held === 1 ? "" : "s"} put on hold — they will not be emailed until approved again` : `${r.approved} approved${r.skipped_already_emailed ? ` · ${r.skipped_already_emailed} skipped (already emailed)` : ""}${r.skipped_greeting ? ` · ${r.skipped_greeting} skipped (greeting)` : ""}`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const approved = roles.reduce((n, r) => n + r.approved, 0);
  const held = roles.reduce((n, r) => n + r.held, 0);
  return (
    <section className="bg-white border border-[#E8E1D2]" data-testid="role-panel">
      <header className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <Users size={15} className="text-[#C84C21] shrink-0" />
        <h3 className="font-serif text-[1.1rem] text-[#0F1825]">By function</h3>
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C6470]" data-testid="role-panel-summary">{approved} approved · {held} on hold</span>
        <span className="ml-auto text-[12px] text-[#5C6470]">Hold a whole function to keep it out of the send queue; approve it again when the wording is right.</span>
      </header>
      <ol>{roles.map((r) => <RoleRow key={r.role} r={r} busy={readOnly ? "ro" : busy} onAct={act} />)}</ol>
    </section>
  );
}
